import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { FormService } from '../../../common/services/form.service';
import { ControlContainer, FormsModule, NgForm } from '@angular/forms';
import { UserService } from 'src/sb-tools/typescript/services/user.service';
import { combineLatest, Subject, take, takeUntil } from 'rxjs';
import { HelperService } from 'src/sb-tools/typescript/services/helper.service';
import { BACKSPACE, COMMA, ENTER, SEMICOLON, SPACE } from 'src/sb-tools/typescript/constants/keycodes';
import { TypeaheadMatch, TypeaheadModule } from 'ngx-bootstrap/typeahead';
import { Field } from 'src/sb-tools/typescript/models/field.model';
import { ClientField } from '../../../common/models/client-field.model';
import { ContactService } from '@sb-tools/typescript/services/contact.service';
import { AddressBookContactDTO } from '@sb-tools/typescript/models/dto/address-book-contact.dto';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { BaseUserWithRole } from '@sb-tools/typescript/models/base-user-with-role.model';
import { InlineSVGComponent } from '../../../../Frontend-Inline-SVG-Module/inline-svg.component';
import { NgClass } from '@angular/common';
import { UserAvatarComponent } from '../../../../Frontend-User-Avatar-Module/user-avatar.component';
import { TruncatePipe } from '@sb-tools/typescript/pipes/truncate.pipe';
import { FocusDirective } from '@sb-tools/typescript/directives/focus.directive';
import { FormFieldLabelComponent } from '@form-field-module/common/components/form-field-label/form-field-label.component';

@Component({
    selector: 'sb-cc-users',
    templateUrl: './cc-users.component.html',
    styleUrls: ['./cc-users.component.scss'],
    viewProviders: [{ provide: ControlContainer, useExisting: NgForm }],
    imports: [
        InlineSVGComponent,
        TranslateModule,
        NgClass,
        UserAvatarComponent,
        FormsModule,
        TruncatePipe,
        FocusDirective,
        TypeaheadModule,
        FormFieldLabelComponent,
    ],
})
export class CcUsersComponent implements OnInit, OnDestroy {
    @Input() formField: Field | ClientField;

    selectedUser = '';
    allUsers: (BaseUserWithRole | AddressBookContactDTO)[] = [];
    selectedUsers: (BaseUserWithRole | AddressBookContactDTO)[] = [];
    separatorKeys = [SPACE, ENTER, COMMA, SEMICOLON];

    private unsubscriber = new Subject<void>();

    constructor(
        public formService: FormService,
        public userService: UserService,
        private contactService: ContactService,
        private ngForm: NgForm,
        private translateService: TranslateService,
    ) {}

    ngOnInit(): void {
        combineLatest([this.contactService.getAllFromCache(), this.userService.getAll()])
            .pipe(takeUntil(this.unsubscriber), take(1))
            .subscribe(([contacts, users]) => {
                contacts.forEach((contact) => {
                    contact.label = contact.name + ' ' + contact.email;
                    contact.typeName = this.translateService.instant('Contacts');
                    contact.isContact = true;
                });
                users.forEach((user) => {
                    user.label = user.name ? user.name + ' ' + user.email : user.email;
                    user.typeName = this.translateService.instant('Teammates');
                });

                this.allUsers = [...contacts, ...users];
            });
    }

    addUser(event: TypeaheadMatch, callback?: () => void) {
        if (
            !this.selectedUsers.find((user) => {
                return user.email.toLowerCase() === event.item.email.toLowerCase();
            })
        ) {
            this.toggleItemSelection(event.value, true);
            this.selectedUsers.push(event.item);
            this.updateFormElementValue();
        }

        if (callback) {
            callback();
        }

        this.selectedUser = '';
    }

    checkAndAddNewUser(callback?: () => void): void {
        if (HelperService.isEmailValid(this.selectedUser)) {
            this.selectedUser = this.selectedUser.toLowerCase();
            const existedUser = this.allUsers.find((user) => {
                return user.email.toLowerCase() === this.selectedUser;
            });

            const newEvent = existedUser
                ? ({
                      item: existedUser,
                      value: existedUser.label,
                  } as TypeaheadMatch)
                : ({
                      item: { email: this.selectedUser, label: this.selectedUser },
                      value: this.selectedUser,
                  } as TypeaheadMatch);

            this.addUser(newEvent, callback);
        }
    }

    keyDownHandler(event) {
        if (
            this.separatorKeys.find((item) => {
                return item === event.keyCode;
            })
        ) {
            const cleanSearchTextCallback = () => {
                event.target.value = '';
            };
            this.checkAndAddNewUser(cleanSearchTextCallback);
            return false;
        }

        if (event && event.keyCode === BACKSPACE && !event.target.value) {
            if (this.selectedUsers.length) {
                this.removeUser(this.selectedUsers.length - 1);
            }
        }
    }

    removeUser(index: number): void {
        this.toggleItemSelection((this.selectedUsers[index] as BaseUserWithRole).label, false);
        this.selectedUsers.splice(index, 1);
        this.updateFormElementValue();
    }

    private toggleItemSelection(label: string, isSelected: boolean): void {
        const selectedItemIndex = this.allUsers.findIndex((selectedItem) => {
            return selectedItem.label === label;
        });

        if (selectedItemIndex > -1) {
            (this.allUsers[selectedItemIndex] as BaseUserWithRole).isSelected = isSelected;
        }
    }

    private updateFormElementValue(): void {
        this.formField.value = this.selectedUsers
            .map((user) => {
                return user.email;
            })
            .join(',');
        // trigger form values changes
        this.ngForm.form.updateValueAndValidity({ onlySelf: false, emitEvent: true });
    }

    ngOnDestroy() {
        this.unsubscriber.next();
        this.unsubscriber.complete();
    }
}
